import { useState } from "react";
import { Truck } from "../types";
import {
  useTrucks,
  useDevTrucks,
  useUserPermissions,
  useUpdateTruckDirection,
  useUpdateLicensePlate,
  useUpdateEntryLicensePlate,
  useDeleteEntry,
  useDeleteTruck,
  useUpdateTruck,
} from "./useQueries";

interface UseTabsDataWithReactQueryProps {
  token?: string;
}

export const useTabsDataWithReactQuery = ({
  token,
}: UseTabsDataWithReactQueryProps) => {
  const [availabilityFilter, setAvailabilityFilter] = useState<
    "all" | "available" | "unavailable"
  >("all");
  const [hitOrMiss, setHitOrMiss] = useState("hit");

  // React Query hooks
  const {
    data: trucks = [],
    isLoading: trucksLoading,
    error: trucksError,
    refetch: refetchTrucks,
    isRefetching: trucksRefetching,
    dataUpdatedAt: trucksUpdatedAt,
  } = useTrucks(token);

  const {
    data: devTrucks = [],
    isLoading: devTrucksLoading,
    error: devTrucksError,
  } = useDevTrucks(hitOrMiss);

  const {
    data: userPermissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useUserPermissions();

  // Mutations
  const updateTruckDirectionMutation = useUpdateTruckDirection();
  const updateLicensePlateMutation = useUpdateLicensePlate();
  const updateEntryLicensePlateMutation = useUpdateEntryLicensePlate();
  const deleteEntryMutation = useDeleteEntry();
  const deleteTruckMutation = useDeleteTruck();
  const updateTruckMutation = useUpdateTruck();

  // Computed loading state
  const loading = trucksLoading || permissionsLoading;

  // Truck operations
  const handleUpdateTruckDirection = async (
    truck: Truck,
    direction: "in" | "out"
  ) => {
    try {
      await updateTruckDirectionMutation.mutateAsync({
        entryId: truck.id,
        direction,
      });
    } catch (error) {
      console.error("Error updating truck direction:", error);
      throw error;
    }
  };

  const handleUpdateTruckLicensePlate = async (
    truck: Truck,
    newLicensePlate: string
  ) => {
    try {
      await updateLicensePlateMutation.mutateAsync({
        truckId: truck.id,
        newLicensePlate,
      });
    } catch (error) {
      console.error("Error updating truck license plate:", error);
      throw error;
    }
  };

  const handleDeleteTruck = async (truck: Truck) => {
    try {
      await deleteTruckMutation.mutateAsync(truck.id);
    } catch (error) {
      console.error("Error deleting truck:", error);
      throw error;
    }
  };

  const handleUpdateTruck = async (truck: Truck) => {
    try {
      await updateTruckMutation.mutateAsync({ truck, token });
    } catch (error) {
      console.error("Error updating truck:", error);
      throw error;
    }
  };

  const handleUpdateEntryLicensePlate = async (
    entryId: string,
    newLicensePlate: string
  ) => {
    try {
      await updateEntryLicensePlateMutation.mutateAsync({
        entryId,
        newLicensePlate,
      });
    } catch (error) {
      console.error("Error updating entry license plate:", error);
      throw error;
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    try {
      await deleteEntryMutation.mutateAsync(entryId);
    } catch (error) {
      console.error("Error deleting entry:", error);
      throw error;
    }
  };

  const handleFilterChange = (filter: "all" | "available" | "unavailable") => {
    setAvailabilityFilter(filter);
  };

  // Utility functions
  const getYardName = (): string => {
    if (!Array.isArray(userPermissions)) return "Yard";

    const yardPermission = userPermissions.find((perm) =>
      perm.startsWith("yard:")
    );
    if (yardPermission) {
      return yardPermission.replace("yard:", "");
    }

    return "Yard";
  };

  const isAdmin =
    Array.isArray(userPermissions) &&
    userPermissions.some((perm) => perm === "level:Admin");

  return {
    // Data
    trucks,
    devTrucks,
    userPermissions,

    // Loading states
    loading,
    trucksLoading,
    devTrucksLoading,
    permissionsLoading,

    // Error states
    trucksError,
    devTrucksError,
    permissionsError,

    // State
    availabilityFilter,
    hitOrMiss,
    setHitOrMiss,

    // Operations
    handleUpdateTruckDirection,
    handleUpdateTruckLicensePlate,
    handleDeleteTruck,
    handleUpdateTruck,
    handleUpdateEntryLicensePlate,
    handleDeleteEntry,
    handleFilterChange,
    refetchTrucks,

    // Utility functions
    getYardName,
    isAdmin,

    // Mutation states
    isUpdatingDirection: updateTruckDirectionMutation.isPending,
    isUpdatingLicensePlate: updateLicensePlateMutation.isPending,
    isDeletingTruck: deleteTruckMutation.isPending,
    isUpdatingTruck: updateTruckMutation.isPending,
    isUpdatingEntryLicensePlate: updateEntryLicensePlateMutation.isPending,
    isDeletingEntry: deleteEntryMutation.isPending,

    // React Query states
    trucksRefetching,
    trucksUpdatedAt,
  };
};
